import { t } from "@lingui/macro";
import { useRef, useState } from "react";
import Papa from "papaparse";
import {
  CheckCircleIcon,
  CircleNotchIcon,
  UploadSimpleIcon,
  UserPlusIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { Button, Input, Switch } from "@reactive-resume/ui";
import { toast } from "sonner";

import { useImportProvisionBatch } from "@/client/services/campaigns/provisioning";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  ASSISTANT D'IMPORT CSV — comptes provisionnés (admin)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Format attendu : 3 colonnes `email, nom, prenom` (noms manquants tolérés).
 *  Chaque adresse devient un compte provisionné (compte réel sans mot de
 *  passe) ; l'utilisateur définit son mot de passe via un lien e-mail à usage
 *  unique (30 j) et est connecté immédiatement. Aucun doublon créé.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** Aperçu client léger (le serveur reste la source de vérité de la validation). */
const EMAIL_RE = /^[^\s@]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/;

const previewCsv = (file: File) =>
  new Promise<{ total: number; valid: number; invalid: number; headers: string[] }>(
    (resolve, reject) => {
      file
        .text()
        .then((content) => {
          const parsed = Papa.parse<Record<string, string>>(content.replace(/^\uFEFF/, ""), {
            header: true,
            skipEmptyLines: "greedy",
          });
          const headers = parsed.meta.fields ?? [];
          const emailCol =
            headers.find((h) => /e-?mail|courriel|address/i.test(h.trim())) ?? headers[0];
          let valid = 0;
          let invalid = 0;
          for (const row of parsed.data) {
            const email = (row[emailCol] ?? "").trim().toLowerCase();
            if (email && EMAIL_RE.test(email)) valid += 1;
            else invalid += 1;
          }
          resolve({ total: valid + invalid, valid, invalid, headers });
        })
        .catch(reject);
    },
  );

export const ImportWizard = ({ onImported }: { onImported: () => void }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [batchName, setBatchName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof previewCsv>> | null>(null);
  const [sendInvites, setSendInvites] = useState(true);
  const [result, setResult] = useState<{
    created: number;
    existing: number;
    invalid: number;
    invitationsQueued: number;
  } | null>(null);
  const importMutation = useImportProvisionBatch();

  const pickFile = async (picked: File | null) => {
    setFile(picked);
    setPreview(null);
    setResult(null);
    if (picked) {
      try {
        setPreview(await previewCsv(picked));
      } catch {
        toast.error("Fichier illisible");
      }
    }
  };

  const submit = async () => {
    if (!file) return;
    try {
      const outcome = await importMutation.mutateAsync({
        file,
        batchName: batchName.trim() || `Import du ${new Date().toLocaleDateString("fr-FR")}`,
        sendInvites,
      });
      setResult({
        created: outcome.created,
        existing: outcome.existing,
        invalid: outcome.invalid,
        invitationsQueued: outcome.invitationsQueued,
      });
      toast.success(
        `${outcome.created.toLocaleString("fr-FR")} compte(s) provisionné(s) — ${outcome.invitationsQueued.toLocaleString("fr-FR")} invitation(s) en file`,
      );
      onImported();
    } catch {
      toast.error(t`Échec de l’import. Vérifiez le format et la taille du fichier CSV (.csv).`);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-xl-admin border border-admin-border bg-admin-surface p-4">
      <p className="flex items-center gap-2 font-semibold text-admin-text-primary">
        <UploadSimpleIcon size={16} />
        {t`Importer des anciens utilisateurs — CSV (.csv)`}
      </p>
      <p className="text-xs text-admin-text-muted">
        Format attendu : 3 colonnes <code className="font-mono">email, nom, prenom</code> — le nom
        est optionnel, l'adresse e-mail fait foi. Les e-mails déjà présents sur la plateforme ne
        créent jamais de doublon ; les comptes internes sont ignorés.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-admin-text-secondary">Nom du lot</label>
          <Input
            value={batchName}
            onChange={(e) => setBatchName(e.target.value)}
            placeholder="Ex. Ex-CV-Connect 2023"
            className="border-admin-border bg-admin-surface-elevated"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-admin-text-secondary">{t`Fichier CSV (.csv) (max 50 000 lignes / 10 Mo)`}</label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            className="hidden"
            onChange={(e) => void pickFile(e.target.files?.[0] ?? null)}
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="justify-start gap-2 border-admin-border bg-admin-surface-elevated"
          >
            <UploadSimpleIcon size={14} />
            {file ? file.name : "Choisir un fichier…"}
          </Button>
        </div>
      </div>

      {preview && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-admin-border bg-admin-surface-elevated p-2.5 text-xs">
          <span className="text-admin-text-secondary">
            {preview.total.toLocaleString("fr-FR")} ligne(s)
          </span>
          <span className="inline-flex items-center gap-1 text-emerald-600">
            <CheckCircleIcon size={12} /> {preview.valid.toLocaleString("fr-FR")} e-mail(s)
            valide(s)
          </span>
          {preview.invalid > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-600">
              <WarningCircleIcon size={12} /> {preview.invalid.toLocaleString("fr-FR")} à rejeter
            </span>
          )}
          <span className="text-admin-text-muted">
            Colonnes : {preview.headers.slice(0, 6).join(", ") || "—"}
          </span>
        </div>
      )}

      <div className="flex items-center justify-between gap-4">
        <label className="flex items-center gap-2 text-sm text-admin-text-secondary">
          <Switch checked={sendInvites} onCheckedChange={setSendInvites} />
          Envoyer les invitations d'activation immédiatement
        </label>
        <Button onClick={submit} disabled={!file || importMutation.isPending} className="gap-2">
          {importMutation.isPending ? (
            <CircleNotchIcon size={14} className="animate-spin" />
          ) : (
            <UserPlusIcon size={14} />
          )}
          Provisionner les comptes
        </Button>
      </div>

      {result && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-700">
          {result.created.toLocaleString("fr-FR")} compte(s) créé(s) ·{" "}
          {result.existing.toLocaleString("fr-FR")} déjà présent(s) sur la plateforme ·{" "}
          {result.invalid.toLocaleString("fr-FR")} ligne(s) invalide(s) ·{" "}
          {result.invitationsQueued.toLocaleString("fr-FR")} invitation(s) en file d'envoi.
        </div>
      )}
    </div>
  );
};
